import React from 'react';
import {
  AlertTriangle, Clock, Cloud, DatabaseBackup, Download, History, Loader2,
  Play, ShieldCheck, Upload, RotateCcw, HardDrive,
} from 'lucide-react';
import { BackupRemoteSettings, BackupRestoreSources } from '../components/BackupRemotePanel';
import {
  BACKUP_STATUS_BADGE, BACKUP_STATUS_LABEL, BACKUP_TRIGGER_LABEL,
  formatBackupDate, formatBackupSize, remoteStatusBadge,
} from '../lib/backupRemote';

/*
 * Ayarlar → Yedekleme, yeni tasarım.
 *
 * Klasik sekme her parçayı alt alta aynı ağırlıkta diziyordu: durum, iki
 * ayrı "şimdi al" düğmesi, dosya listesi ve geçmiş birbirinden ayırt
 * edilmiyordu. Burada sıra işin sırasıdır: önce durum (dört özet), sonra
 * eylem (tek şerit), sonra ayarlar ve dosyalar, en sonda tehlikeli işlem
 * (geri yükleme) ve geçmiş. Veri ve istekler Settings.jsx'tekiyle aynıdır;
 * bileşen yalnızca çizer, durum ona dışarıdan verilir.
 */
export const RESTORE_CONFIRM_PHRASE = 'GERİ YÜKLE';

const Tile = ({ icon: Icon, label, value, sub, tone = '', action = null }) => (
  <div className={`bk-tile ${tone}`}>
    <div className="bk-tile-label"><Icon size={14} aria-hidden="true" /> {label}</div>
    <div className="bk-tile-value">{value}</div>
    {sub && <div className="bk-tile-sub">{sub}</div>}
    {action}
  </div>
);

const FileRow = ({ icon: Icon = DatabaseBackup, file, busy, onDownload }) => (
  <li className="bk-file">
    <Icon size={15} className="bk-file-icon" aria-hidden="true" />
    <div className="bk-file-main">
      <div className="bk-file-name">{file.filename}</div>
      <div className="bk-file-meta">
        {formatBackupSize(file.size_bytes)} · {formatBackupDate(file.created_at)}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onDownload(file.filename)}
      disabled={busy}
      className="admin-btn-secondary bk-btn-sm"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} İndir
    </button>
  </li>
);

const BackupPanel = ({
  s, setS, status, busy, running, downloadBusy, snapshotFormat, setSnapshotFormat,
  jobWatch, restoreFile, setRestoreFile, onRunBackup, onSnapshot, onDownload,
  onReload, onRestoreSourceStarted, onRestoreFile,
}) => {
  const retentionDays = status?.retention_days || 3;
  const canDump = Boolean(status?.mysqldump_available);
  const canRestore = Boolean(status?.mysql_available);
  const remote = status?.remote;
  const lastLog = status?.logs?.[0];
  const hour = String(Number(s.backup_hour ?? 14)).padStart(2, '0');
  const snapshotRunning = jobWatch?.kind === 'snapshot';
  const restoreRunning = jobWatch?.kind === 'restore';

  const remoteTile = remote?.ready
    ? { value: 'Açık', tone: 'is-ok', sub: `Her yedek R2'ye de gider · ${retentionDays} gün` }
    : remote?.enabled
      ? { value: 'Bilgiler eksik', tone: 'is-warn', sub: 'Aşağıdaki R2 kartını tamamlayın' }
      : { value: 'Kapalı', tone: '', sub: 'Yedek yalnızca sunucuda durur' };

  return (
    <div className="bk">
      {status && !canDump && (
        <div className="settings-note is-danger">
          <AlertTriangle size={16} />
          <div>
            <b>mysqldump sunucuda bulunamadı.</b> Yedek alınamaz. MySQL istemci
            araçlarını kurun ya da <code>MYSQLDUMP_PATH</code> ortam değişkeniyle tam
            yolu belirtin.
          </div>
        </div>
      )}
      {status && !canRestore && (
        <div className="settings-note is-warn">
          <AlertTriangle size={16} />
          <div>
            <code>mysql</code> istemcisi bulunamadı; yedek alınabilir ama <b>geri
            yükleme yapılamaz</b>. Tam yolu <code>MYSQL_CLIENT_PATH</code> ortam
            değişkeniyle verebilirsiniz.
          </div>
        </div>
      )}

      {/* 1. Durum */}
      <section className="bk-tiles" aria-label="Yedek durumu">
        <Tile
          icon={Clock}
          label="Otomatik yedek"
          value={s.backup_enabled ? `Her gün ${hour}:00` : 'Kapalı'}
          tone={s.backup_enabled ? 'is-ok' : ''}
          sub={s.backup_enabled && status?.next_run_at
            ? `Sıradaki: ${formatBackupDate(status.next_run_at)}`
            : 'Açmak için aşağıdaki ayarı kullanın'}
        />
        <Tile
          icon={History}
          label="Son çalışma"
          value={formatBackupDate(status?.last_run_at)}
          sub={lastLog ? (
            <span className={`badge-pill ${BACKUP_STATUS_BADGE[lastLog.status] || 'badge-yellow'}`}>
              {BACKUP_STATUS_LABEL[lastLog.status] || lastLog.status}
            </span>
          ) : 'Henüz yedek alınmadı'}
        />
        <Tile
          icon={HardDrive}
          label="Sunucudaki son yedek"
          value={status?.current_file ? formatBackupSize(status.current_file.size_bytes) : 'Dosya yok'}
          sub={status?.current_file?.filename || `Dosyalar ${retentionDays} gün saklanır`}
          action={status?.current_file ? (
            <button
              type="button"
              onClick={() => onDownload()}
              disabled={downloadBusy === status.current_file.filename}
              className="bk-tile-action"
            >
              {downloadBusy === status.current_file.filename
                ? <Loader2 size={13} className="animate-spin" />
                : <Download size={13} />} İndir
            </button>
          ) : null}
        />
        <Tile icon={Cloud} label="Uzak yedek (R2)" value={remoteTile.value} tone={remoteTile.tone} sub={remoteTile.sub} />
      </section>

      {/* 2. Eylem */}
      <section className="bk-card bk-run" aria-label="Şimdi yedek al">
        <div className="bk-run-copy">
          <h3>Şimdi yedek al</h3>
          <p>
            Yedek veritabanı dökümü ve yükleme klasörüdür (görseller, dekontlar, KYC
            belgeleri). <b>Sunucuda al</b> günlük işin aynısını hemen çalıştırır;
            <b> İndir</b> dosyayı doğrudan tarayıcınıza verir.
          </p>
        </div>
        <div className="bk-run-actions">
          <button
            type="button"
            onClick={onRunBackup}
            disabled={busy || running || !canDump}
            className="admin-btn-secondary bk-btn"
          >
            {(busy || running) && !snapshotRunning
              ? <Loader2 size={14} className="animate-spin" />
              : <Play size={14} />}
            {running && !snapshotRunning ? 'Yedek alınıyor…' : 'Sunucuda al'}
          </button>
          <div className="bk-split">
            <div className="bk-seg" role="group" aria-label="İndirilecek dosya türü">
              {[['zip', 'ZIP', 'Veritabanı + dosyalar'], ['sql', 'SQL', 'Yalnızca veritabanı']].map(([value, label, title]) => (
                <button
                  key={value}
                  type="button"
                  title={title}
                  aria-pressed={snapshotFormat === value}
                  className={snapshotFormat === value ? 'is-on' : ''}
                  onClick={() => setSnapshotFormat(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onSnapshot}
              disabled={busy || running || !canDump}
              className="admin-btn-primary bk-btn"
            >
              {(busy || snapshotRunning) ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {snapshotRunning ? 'Hazırlanıyor…' : 'Al ve indir'}
            </button>
          </div>
        </div>
      </section>

      {/* 3. Ayarlar ve dosyalar */}
      <div className="bk-grid">
        <section className="bk-card">
          <header className="bk-card-head">
            <h3><Clock size={15} aria-hidden="true" /> Otomatik günlük yedek</h3>
          </header>
          <div className="settings-toggles">
            <label className="settings-toggle">
              <input type="checkbox" checked={!!s.backup_enabled} onChange={(e) => setS({ ...s, backup_enabled: e.target.checked })} />
              <span>
                <span className="block">Her gün otomatik yedek al</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Kapalıyken de {retentionDays} günü geçen dosyalar sunucudan ve R2&apos;den silinir.
                </span>
              </span>
            </label>
            <label className="settings-toggle">
              <input type="checkbox" checked={s.backup_include_uploads !== false} onChange={(e) => setS({ ...s, backup_include_uploads: e.target.checked })} />
              <span>
                <span className="block">Yükleme klasörünü dahil et</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Görseller, dekontlar ve KYC belgeleri. Kapatılırsa geri yüklemede bunlar dönmez.
                </span>
              </span>
            </label>
          </div>
          <label className="bk-field">
            <span>Yedekleme saati (Türkiye saati)</span>
            <select
              value={Number(s.backup_hour ?? 14)}
              onChange={(e) => setS({ ...s, backup_hour: Number(e.target.value) })}
              className="admin-input admin-input-w-md"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </label>
          <p className="bk-foot">
            Değişiklik sayfanın altındaki <b>Kaydet</b> ile yayına girer. Geçmiş{' '}
            {status?.log_retention_days || 7} gün tutulur.
          </p>
        </section>

        <section className="bk-card">
          <header className="bk-card-head">
            <h3><HardDrive size={15} aria-hidden="true" /> Sunucudaki dosyalar</h3>
            <span className="bk-count">{status?.files?.length || 0}</span>
          </header>
          {status?.files?.length ? (
            <ul className="bk-files">
              {status.files.map((file) => (
                <FileRow key={file.filename} file={file} busy={downloadBusy === file.filename} onDownload={onDownload} />
              ))}
            </ul>
          ) : (
            <p className="bk-empty">
              Sunucuda yedek dosyası yok. <b>Sunucuda al</b> ile ilk yedeği oluşturun.
            </p>
          )}
        </section>
      </div>

      {/* 4. Uzak yedek */}
      <div className="bk-remote">
        <BackupRemoteSettings remote={remote} retentionDays={retentionDays} onSaved={onReload} />
        <div className="settings-note is-warn">
          <AlertTriangle size={16} />
          <div>
            Yedek <b>dekontları ve KYC belgelerini</b> taşır. Kova herkese açık
            yapılmamalı, anahtar yalnızca bu kovaya yetkili olmalıdır.
          </div>
        </div>
      </div>

      {/* 5. Geri yükleme — tehlikeli işlem en sonda ve ayrı tonda */}
      <section className="bk-card is-danger" aria-label="Yedeği geri yükle">
        <header className="bk-card-head">
          <h3><RotateCcw size={15} aria-hidden="true" /> Yedeği geri yükle</h3>
        </header>
        <div className="bk-danger-note">
          <AlertTriangle size={16} aria-hidden="true" />
          <p>
            <b>Mevcut veriler dosyadakilerle değiştirilir.</b> İşlemden hemen önce
            otomatik bir güvenlik kopyası alınır; alınamazsa geri yükleme başlamaz.
            Her geri yükleme sizden <b>{RESTORE_CONFIRM_PHRASE}</b> yazarak onay ister.
          </p>
        </div>

        <BackupRestoreSources
          status={status}
          disabled={busy || running || !canRestore}
          confirmPhrase={RESTORE_CONFIRM_PHRASE}
          onStarted={onRestoreSourceStarted}
        />

        <div className="bk-upload">
          <div className="bk-upload-copy">
            <h4>Bilgisayarınızdan yükleyin</h4>
            <p>
              <b>.sql</b> yalnızca veritabanını yazar; <b>.zip</b> içinde <code>database.sql</code>
              {' '}ve/veya <code>uploads/</code> varsa ikisini de geri yükler. En fazla{' '}
              {status?.restore_max_mb || 2048} MB.
            </p>
          </div>
          <div className="bk-upload-row">
            <label className="bk-drop">
              <Upload size={15} aria-hidden="true" />
              <span>{restoreFile ? restoreFile.name : 'Dosya seç (.zip / .sql)'}</span>
              <input
                type="file"
                accept=".zip,.sql"
                className="hidden"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              />
            </label>
            <button
              type="button"
              onClick={onRestoreFile}
              disabled={busy || running || !restoreFile || !canRestore}
              className="admin-btn-danger bk-btn"
            >
              {(busy || restoreRunning) ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {restoreRunning ? 'Geri yükleniyor…' : 'Geri yükle'}
            </button>
          </div>
        </div>

        {!!status?.safety_files?.length && (
          <div className="bk-safety">
            <h4>Geri yükleme öncesi alınan güvenlik kopyaları</h4>
            <ul className="bk-files">
              {status.safety_files.map((file) => (
                <FileRow key={file.filename} icon={ShieldCheck} file={file} busy={downloadBusy === file.filename} onDownload={onDownload} />
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* 6. Geçmiş */}
      <section className="bk-card" aria-label="Yedekleme geçmişi">
        <header className="bk-card-head">
          <h3><History size={15} aria-hidden="true" /> Yedekleme geçmişi</h3>
        </header>
        {!status?.logs?.length ? <p className="bk-empty">Kayıt yok.</p> : (
          <div className="bk-table-wrap">
            <table className="admin-table bk-table">
              <thead>
                <tr>
                  <th>Dosya</th>
                  <th>Başlangıç</th>
                  <th>İş</th>
                  <th className="bk-num">Boyut</th>
                  <th>R2</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {status.logs.map((log) => {
                  const remoteBadge = remoteStatusBadge(log);
                  return (
                    <tr key={log.id}>
                      <td>
                        <div className="bk-file-name">
                          {log.filename || (log.status === 'failed' ? 'Alınamadı' : 'Sürüyor…')}
                        </div>
                        {(log.error || (log.remote_status === 'failed' && log.remote_error)) && (
                          <div className="bk-error">
                            {log.error || `R2: ${log.remote_error}`}
                          </div>
                        )}
                      </td>
                      <td className="bk-nowrap">{formatBackupDate(log.started_at)}</td>
                      <td className="bk-nowrap">
                        {BACKUP_TRIGGER_LABEL[log.trigger] || log.trigger}
                        {log.trigger !== 'schedule' && log.admin_username ? ` · ${log.admin_username}` : ''}
                      </td>
                      <td className="bk-num bk-nowrap">
                        {log.size_bytes ? formatBackupSize(log.size_bytes) : '—'}
                      </td>
                      <td>
                        {remoteBadge ? (
                          <span className={`badge-pill ${remoteBadge.badge}`} title={log.remote_error || log.remote_key || undefined}>
                            {remoteBadge.label}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`badge-pill ${BACKUP_STATUS_BADGE[log.status] || 'badge-yellow'}`}>
                          {BACKUP_STATUS_LABEL[log.status] || log.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default BackupPanel;
